/**
 * Code-side mapping that supplies the structured-data fields the CMS does not
 * reliably store per page (schema.org @type, serviceType, and audience).
 *
 * Keyed by the CMS `topic` value that the topic-page fetch is filtered on
 * (Mortgage, Relocation, HomeServices, Programs). Only topics listed here emit
 * a Service schema — other topic pages (About, Guidance, ...) are intentionally
 * absent so they produce no service markup.
 *
 * Individual programs live under a single "Programs" topic, so their specifics
 * are overridden by slug.
 */

import { getSiteURL } from '@lib/utils';

// Sibcy Cline's service footprint. Used for `areaServed` on every service page.
// Kept broad-but-accurate per the AEO guidance (do not overgeneralize).
export const SERVICE_AREAS = [
  'Cincinnati, Ohio',
  'Dayton, Ohio',
  'Northern Kentucky',
  'Southeast Indiana',
  'Ohio',
  'Kentucky',
  'Indiana'
];

// The same footprint grouped by state for the on-page "Where We Serve" section.
// Presenting statewide coverage with its primary metros/regions nested avoids the
// flat list's visual redundancy (e.g. "Kentucky" and "Northern Kentucky" side by
// side) while keeping every SERVICE_AREAS value verifiable on the page for schema
// parity. Keep in sync with SERVICE_AREAS above.
export const SERVICE_AREA_REGIONS = [
  { state: 'Ohio', areas: ['Cincinnati', 'Dayton'] },
  { state: 'Kentucky', areas: ['Northern Kentucky'] },
  { state: 'Indiana', areas: ['Southeast Indiana'] }
];

// The provider behind every service/program page.
export const SERVICE_PROVIDER_NAME = 'Sibcy Cline REALTORS®';

// Resolved per request so the provider url shares an origin with the page url
// (which comes from getSiteURL). A hardcoded production url would otherwise pair a
// beta/preview page with a www provider in the same node.
export const getServiceProvider = () => ({
  name: SERVICE_PROVIDER_NAME,
  url: getSiteURL()
});

// Defaults per CMS topic. `schemaType` is the schema.org @type for the service.
// Mortgage and home services use a multi-type node so the specific business type
// (FinancialService / HomeAndConstructionBusiness) is retained while the
// service-specific properties (serviceType, provider, audience) remain valid via
// the Service type — otherwise the schema.org validator flags them as unknown
// fields on those LocalBusiness subtypes.
//
// `introPhrase` is the object of "Sibcy Cline REALTORS® provides ___ across the
// communities we call home" in the visible Where We Serve section. It is authored
// per entry rather than derived from `serviceType`, because the serviceType values
// are noun labels ("Home warranty program") that do not read as verb-phrase objects.
// It still carries the serviceType wording so the schema value stays verifiable
// on the page. An entry without one falls back to a generic sentence.
const TOPIC_DEFAULTS = {
  Mortgage: {
    schemaType: ['Service', 'FinancialService'],
    serviceType: 'Mortgage and home financing',
    audience: 'Home buyers and homeowners',
    introPhrase: 'mortgage and home financing'
  },
  Relocation: {
    schemaType: 'Service',
    serviceType: 'Relocation services',
    audience: 'Relocating employees and families',
    introPhrase: 'relocation services'
  },
  HomeServices: {
    schemaType: ['Service', 'HomeAndConstructionBusiness'],
    serviceType: 'Home services',
    audience: 'Homeowners',
    introPhrase: 'home services'
  },
  Programs: {
    schemaType: 'Service',
    serviceType: 'Real estate program',
    introPhrase: 'real estate programs'
  }
};

// Per-slug overrides for individual programs (all served under the Programs topic).
const PROGRAM_OVERRIDES = {
  quickbuy: {
    serviceType: 'Guaranteed cash offer program',
    audience: 'Home sellers',
    introPhrase: 'our QuickBuy guaranteed cash offer program'
  },
  'home-warranty': {
    serviceType: 'Home warranty program',
    audience: 'Home buyers and sellers',
    introPhrase: 'our home warranty program'
  },
  'military-on-the-move': {
    serviceType: 'Military relocation rebate program',
    audience: 'Military service members, veterans, and their families',
    introPhrase: 'our Military on the Move relocation rebate program'
  }
};

/**
 * Resolve the service-schema configuration for a topic page, or `null` when the
 * page is not a service/program page (so no schema should be emitted).
 *
 * @param {string} topic - CMS topic value (e.g. "Mortgage", "Programs")
 * @param {string} [slug] - page slug, used for per-program overrides
 * @returns {{schemaType: string, serviceType?: string, audience?: string}|null}
 */
export const getServiceSchemaConfig = (topic, slug) => {
  const base = TOPIC_DEFAULTS[topic];
  if (!base) return null;

  const override = topic === 'Programs' && slug ? PROGRAM_OVERRIDES[slug] : null;

  return {
    ...base,
    ...override
  };
};
